import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  type ApolloCache,
} from '@apollo/client'
import { SetContextLink } from '@apollo/client/link/context'
import { ErrorLink } from '@apollo/client/link/error'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { getMainDefinition } from '@apollo/client/utilities'
import { createClient as createGraphqlWsClient } from 'graphql-ws'
import { getOperationAST, type DocumentNode } from 'graphql'
import { map } from 'rxjs'
import { toast } from 'ui'
import Sentry from './sentry'
import {
  errorWithCode,
  graphQLErrorsOf,
  hasErrorCode,
  networkErrorOf,
} from './apolloErrors'
import { createApolloCache } from './apolloCache'
import { APOLLO_DEFAULT_OPTIONS } from './apolloDefaults'
import {
  RevertToAccountingAccountDocument,
  SelfDocument,
} from '../operations-types'
import { grantsFromList } from './permissions'
import {
  explainRefusal,
  findRefusal,
  refusalToastId,
  type Refusal,
} from './refusal'
import { reportRefusedSession, reportStaleTokenRefusal } from './refusedSession'
import { removeRfiDrafts } from '../components/onboarding/rfi/rfiDraftStorage'

/**
 * Module-singleton Apollo client: the auth header is read from localStorage
 * per request, so the client and its cache survive identity changes, which
 * are handled explicitly by onAuthTokenChanged/onSignedOut. Never construct a
 * second client, with one exception: the hosted partner enrollment flow's
 * (components/partnerEnrollment/enrollmentSession.ts), a different identity
 * that must never write this client's token, trip its sign-out on refusal,
 * or share its cache.
 */

/**
 * The operation name rides the URL only for observability (devtools, Sentry
 * breadcrumbs); the server reads the operation from the body. Never put
 * variables here: they carry customer PII and would leak into proxy logs and
 * Referer.
 */
export const graphqlUri = (operationName: string | undefined) =>
  `/api/graphql?op=${operationName || 'anonymous'}`

const httpLink = new HttpLink({
  uri: (operation) => graphqlUri(operation.operationName),
})

/**
 * An explicit Authorization header on the operation wins: "act as user" flows
 * pass a becomeUser token via context, and overriding it would silently run
 * the mutation as the admin's own organization.
 */
export const withAuthHeaders = (
  headers: Record<string, string> | undefined,
  token: string | null,
): { headers: Record<string, string> | undefined } => {
  const hasExplicitAuthorization = Object.keys(headers ?? {}).some(
    (key) => key.toLowerCase() === 'authorization',
  )
  if (!token || hasExplicitAuthorization) return { headers }
  return {
    headers: {
      ...headers,
      authorization: `Bearer ${token}`,
    },
  }
}

export const authLink = new SetContextLink(({ headers }) =>
  withAuthHeaders(headers, localStorage.getItem('token')),
)

/**
 * An accountant session whose access to the current client has ended while
 * the accountant's own standing is fine (HTTP 403; Client Access V1 design
 * §5.1 "When access ends"). Every gate refuses with it except
 * revertToAccountingAccount, so the session can go back to the firm instead
 * of being signed out. Takes a hook's error or the error link's.
 */
export const isClientAccessEndedError = (failure: unknown): boolean =>
  hasErrorCode(failure, 'CLIENT_ACCESS_ENDED')

// The error link lives outside React, so AuthProvider registers its signOut
// here. Before it does, the fallback clears storage only, and the full-page
// redirect rebuilds auth state from that.
let onSessionExpired: (() => void) | null = null
export const registerSessionExpiredHandler = (handler: () => void) => {
  onSessionExpired = handler
}

/**
 * What the API said when it refused the session, its code and sentence, and
 * the operation it refused. Subscriptions ride the websocket link, outside the
 * error link, so a refused socket never ends a session here.
 */
type SessionRefusal = { reason: string; detail?: string; operation: string }

const endSession = (refusal: SessionRefusal) => {
  // Read before the sign-out clears it. A page's reads fail together, and
  // only the first refusal still finds the token, so one ending is one report.
  try {
    const token = localStorage.getItem('token')
    if (token) {
      reportRefusedSession({ surface: 'error_link', ...refusal, token })
    }
  } catch {
    // Reporting must never keep a refused session on screen.
  }
  if (onSessionExpired) {
    onSessionExpired()
  } else {
    localStorage.removeItem('token')
    localStorage.removeItem('email')
    removeRfiDrafts()
  }
  if (window.location.pathname !== '/login') {
    const redirect =
      window.location.pathname + window.location.search + window.location.hash
    window.location.assign(`/login?redirect=${encodeURIComponent(redirect)}`)
  }
}

// Registered by AuthProvider. Resolves false when there is no way back to the
// firm, and the session then ends as an expired one does.
let onClientAccessEnded: (() => Promise<boolean>) | null = null
export const registerClientAccessEndedHandler = (
  handler: () => Promise<boolean>,
) => {
  onClientAccessEnded = handler
}

const leaveEndedClient = async (refusal: SessionRefusal) => {
  let returned = false
  try {
    returned = (await onClientAccessEnded?.()) === true
  } catch {
    // A handler that threw has not moved the tab.
  }
  if (!returned) {
    endSession(refusal)
  }
}

function sentToken(operation: ApolloLink.Operation): string | null {
  const headers: Record<string, string> = operation.getContext().headers ?? {}
  const key = Object.keys(headers).find(
    (name) => name.toLowerCase() === 'authorization',
  )
  return key ? headers[key].replace(/^Bearer\s+/i, '') : null
}

function readStoredToken(): string | null {
  try {
    return localStorage.getItem('token')
  } catch {
    return null
  }
}

/**
 * The stored session when a refusal was not of it, so it must not be ended
 * for it. The browser has one session that every tab sends and every tab
 * follows (auth.tsx), so ending it on a refusal of an older token signs out
 * the login that replaced that token, here and in every other tab: a request
 * sent before a sign-in in another tab, or a refetch Apollo deduped onto one
 * (auth.tsx `switchSession`). A caller's explicit Authorization is likewise
 * not the stored session. With nothing stored there is no newer session to
 * keep, and the refusal ends this tab's as before.
 */
function otherStoredSession(operation: ApolloLink.Operation): string | null {
  const stored = readStoredToken()
  return stored !== null && stored !== sentToken(operation) ? stored : null
}

// A refused revert must not hand off to the handler again, or it would loop.
const REVERT_OPERATION = getOperationAST(RevertToAccountingAccountDocument)
  ?.name?.value

export function createErrorLink(): ApolloLink {
  // Handled once per page: a page's reads fail together and Apollo reports a
  // 403 with data twice. The latch never reopens, because handling always
  // ends the page, and a refusal landing after the switch must not send
  // another revert under the firm's session.
  let clientAccessEndedHandled = false
  const staleTokensReported = new Set<string | null>()

  /**
   * True when the refusal was of a token other than the stored one, which the
   * link then only reports. The request fails as sent: replaying it under the
   * stored session could run a write as an identity it was not sent as.
   */
  const refusedStaleToken = (
    operation: ApolloLink.Operation,
    refusal: SessionRefusal,
  ): boolean => {
    const stored = otherStoredSession(operation)
    if (stored === null) {
      return false
    }
    const sent = sentToken(operation)
    if (!staleTokensReported.has(sent)) {
      staleTokensReported.add(sent)
      try {
        reportStaleTokenRefusal({
          code: refusal.reason,
          detail: refusal.detail,
          operation: refusal.operation,
          sentToken: sent,
          storedToken: stored,
        })
      } catch {
        // Reporting must never change what the link does.
      }
    }
    return true
  }

  return new ErrorLink(({ error, operation }) => {
    const accessEnded = errorWithCode(error, 'CLIENT_ACCESS_ENDED')
    if (accessEnded && operation.operationName !== REVERT_OPERATION) {
      const refusal = {
        reason: 'CLIENT_ACCESS_ENDED',
        detail: accessEnded.message,
        operation: operation.operationName || 'anonymous',
      }
      // The latch first: after the switch to the firm, every straggler is a
      // stale token, and none of them is news.
      if (!clientAccessEndedHandled && !refusedStaleToken(operation, refusal)) {
        clientAccessEndedHandled = true
        void leaveEndedClient(refusal)
      }
      return
    }

    const sessionRefused = errorWithCode(error, 'UNAUTHENTICATED')
    if (sessionRefused) {
      const refusal = {
        reason: 'UNAUTHENTICATED',
        detail: sessionRefused.message,
        operation: operation.operationName || 'anonymous',
      }
      if (!refusedStaleToken(operation, refusal)) {
        endSession(refusal)
      }
      return
    }

    // A bare `Failed to fetch` is otherwise unattributable in Sentry; a tag
    // stays searchable. Name-only: operation.variables carry customer PII.
    const sentryContext = {
      tags: { graphql_operation: operation.operationName || 'anonymous' },
    }

    graphQLErrorsOf(error).forEach((graphQLError) => {
      Sentry.captureException(graphQLError.message, sentryContext)
    })

    const networkError = networkErrorOf(error)
    if (networkError) {
      Sentry.captureException(networkError, sentryContext)
    }
  })
}

/**
 * The refusal surface (docs/workspace-roles/design.md §9.1, R1). The API
 * refuses in the payload with HTTP 200, so nothing above this link sees a
 * failure. A refusal is explained from OPERATION_AUTHZ and the Self cache's
 * grants, and reported to Sentry: since no control hides outside banking, a
 * refusal is a role-design signal, counted per operation. The toast is keyed
 * by operation so duplicates and retries collapse into one.
 */
export const refusalLink = new ApolloLink((operation, forward) =>
  forward(operation).pipe(
    map((response) => {
      const refusal = findRefusal(
        response.data as Record<string, unknown> | null | undefined,
      )
      if (refusal) {
        const explained = explainRefusal({
          document: operation.query,
          variables: operation.variables,
          grants: grantsFromList(readSelfGrants(operation.client.cache)),
          apiMessage: refusal.error.message,
        })
        surfaceRefusal(
          operation.operationName ?? '',
          explained,
          readSelfOrganizationId(operation.client.cache),
          isMutation(operation.query),
        )
      }
      return response
    }),
  ),
)

function isMutation(query: DocumentNode): boolean {
  const definition = getMainDefinition(query)
  return (
    definition.kind === 'OperationDefinition' &&
    definition.operation === 'mutation'
  )
}

function readSelfGrants(cache: ApolloCache) {
  try {
    return cache.readQuery({ query: SelfDocument })?.user?.user?.effectiveGrants
  } catch {
    return undefined
  }
}

function readSelfOrganizationId(cache: ApolloCache): string | undefined {
  try {
    return cache.readQuery({ query: SelfDocument })?.user?.user?.organization
      ?.id
  } catch {
    return undefined
  }
}

export function surfaceRefusal(
  operationName: string,
  refusal: Refusal,
  organizationId?: string,
  shown = true,
) {
  if (shown) {
    toast.error(refusal.message, { id: refusalToastId(operationName) })
  }
  // Name-only: variables carry customer PII. Missing keys are catalog keys.
  Sentry.captureMessage('A permission refusal reached the client', {
    level: 'info',
    tags: {
      graphql_operation: operationName || 'anonymous',
      refusal_kind: refusal.kind,
      refusal_shown: shown ? 'toast' : 'page',
      ...(refusal.kind === 'ungated' ? { refusal_reason: refusal.reason } : {}),
      ...(refusal.kind === 'unexplained' && refusal.unmapped
        ? { refusal_unmapped: 'true' }
        : {}),
    },
    extra: {
      organizationId,
      missingKeys: refusal.kind === 'missing-keys' ? refusal.missing : [],
    },
  })
}

/**
 * In dev the socket rides the same origin and the Vite proxy carries the
 * upgrade. In production it connects to VITE_API_HOST directly, because
 * Vercel's rewrite of /api carries HTTP but not websocket upgrades.
 */
const getGraphqlWsUrl = () => {
  const apiHost = import.meta.env.VITE_API_HOST as string | undefined
  const url = apiHost
    ? new URL('/api/graphql', apiHost)
    : new URL('/api/graphql', window.location.href)
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
  return url.toString()
}

// Authenticates via connectionParams since websocket upgrades can't carry an
// Authorization header. The token is read at connect time, so terminating the
// lazy socket on an identity change is enough.
const wsClient = createGraphqlWsClient({
  url: getGraphqlWsUrl,
  lazy: true,
  retryAttempts: Number.POSITIVE_INFINITY,
  shouldRetry: () => true,
  connectionParams: () => {
    const token = localStorage.getItem('token')
    return token ? { authorization: `Bearer ${token}` } : {}
  },
})

export const apolloClient = new ApolloClient({
  link: ApolloLink.split(
    ({ query }) => {
      const definition = getMainDefinition(query)
      return (
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription'
      )
    },
    new GraphQLWsLink(wsClient),
    ApolloLink.from([createErrorLink(), refusalLink, authLink, httpLink]),
  ),
  cache: createApolloCache(),
  devtools: { enabled: import.meta.env.DEV },
  defaultOptions: APOLLO_DEFAULT_OPTIONS,
})

/**
 * SECURITY: these are the only Apollo-cache wipes between identities. Every
 * login / become-user / revert / sign-out path must funnel through one of
 * them, or cached data leaks across identities. Callers write the new token
 * to storage first.
 */
export async function onAuthTokenChanged() {
  wsClient.terminate()
  // resetStore also refetches active queries as the new identity; their
  // failures surface through the queries, so the rejection is redundant.
  await apolloClient.resetStore().catch(() => undefined)
}

export async function onSignedOut() {
  wsClient.terminate()
  // clearStore wipes without refetching — the caller is navigating to /login.
  await apolloClient.clearStore().catch(() => undefined)
}
